import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Avatar
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/api';

const Profile = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/submissions/user/stats`,
        { withCredentials: true }
      );
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateCalendarHeatmap = () => {
    if (!stats || !stats.submissionActivity) return { monthGroups: [] };

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29); // 30 days total including today

    // Create array of all days (left to right, oldest to newest)
    const allDays = [];
    for (let i = 0; i < 30; i++) {
      const date = new Date(thirtyDaysAgo);
      date.setDate(date.getDate() + i);
      const dateKey = date.toISOString().split('T')[0];
      const count = stats.submissionActivity[dateKey] || 0;
      const isToday = dateKey === today.toISOString().split('T')[0];
      
      allDays.push({
        date: dateKey,
        count,
        isToday,
        month: date.toLocaleDateString('en-US', { month: 'short' }),
        dayOfMonth: date.getDate()
      });
    }

    // Group days by month
    const monthGroups = [];
    let currentMonth = null;
    let currentGroup = [];

    allDays.forEach((day) => {
      if (day.month !== currentMonth) {
        if (currentMonth !== null && currentGroup.length > 0) {
          // Save previous month group
          monthGroups.push({
            month: currentMonth,
            days: [...currentGroup]
          });
        }
        currentMonth = day.month;
        currentGroup = [day];
      } else {
        currentGroup.push(day);
      }
    });

    // Add last month group
    if (currentGroup.length > 0) {
      monthGroups.push({
        month: currentMonth,
        days: currentGroup
      });
    }

    return { monthGroups };
  };

  const getIntensityColor = (count) => {
    if (count === 0) return '#ebedf0';
    if (count === 1) return '#c6e48b';
    if (count === 2) return '#7bc96f';
    if (count === 3) return '#239a3b';
    return '#196127'; // 4+
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
    }
    const months = Math.floor(diffDays / 30);
    return months === 1 ? '1 month ago' : `${months} months ago`;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ marginTop: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!stats) {
    return (
      <Container maxWidth="lg" sx={{ marginTop: 4 }}>
        <Typography variant="h6">Failed to load profile statistics</Typography>
      </Container>
    );
  }

  const calendarDays = generateCalendarHeatmap();

  return (
    <Container maxWidth="lg" sx={{ marginTop: 4, marginBottom: 4 }}>
      <Typography variant="h4" gutterBottom>
        Profile
      </Typography>

      <Grid container spacing={3}>
        {/* Left Sidebar - User Info and Stats */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 3 }}>
              {user.picture ? (
                <Avatar src={user.picture} alt={user.name} sx={{ width: 64, height: 64 }} />
              ) : (
                <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}>
                  {user.name?.charAt(0).toUpperCase()}
                </Avatar>
              )}
              <Box>
                <Typography variant="h6">{user.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.email}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="h5" gutterBottom>
                {stats.totalSolved}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Questions Solved
              </Typography>
            </Box>

            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="h5" gutterBottom>
                {stats.totalSubmissions}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Submissions
              </Typography>
            </Box>

            <Box sx={{ marginBottom: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WhatshotIcon sx={{ color: '#ff4444', fontSize: 28 }} />
                <Typography variant="h5" gutterBottom sx={{ margin: 0 }}>
                  {stats.currentStreak || 0}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Current Streak (days)
              </Typography>
            </Box>

            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="h5" gutterBottom>
                {stats.maxStreak}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Max Streak (days)
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom>
                {stats.activeDays}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Days (last 30 days)
              </Typography>
            </Box>
          </Paper>

          {/* Difficulty Breakdown */}
          <Paper elevation={3} sx={{ padding: 3 }}>
            <Typography variant="h6" gutterBottom>
              Difficulty Breakdown
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1">Easy</Typography>
                <Chip label={stats.difficultyBreakdown.Easy} color="info" size="small" />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1">Medium</Typography>
                <Chip label={stats.difficultyBreakdown.Medium} color="warning" size="small" />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1">Hard</Typography>
                <Chip label={stats.difficultyBreakdown.Hard} color="error" size="small" />
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Right Section - Calendar and Recent Submissions */}
        <Grid item xs={12} md={8}>
          {/* Submission Activity Calendar */}
          <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
            <Typography variant="h6" gutterBottom>
              Submission Activity (Last 30 Days)
            </Typography>
            <Box sx={{ marginTop: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Calendar with month groups */}
                {calendarDays.monthGroups && calendarDays.monthGroups.length > 0 ? (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    {calendarDays.monthGroups.map((group, groupIndex) => {
                      // Calculate the middle index of the group
                      const middleIndex = Math.floor(group.days.length / 2);
                      
                      return (
                        <Box
                          key={group.month}
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                            alignItems: 'center',
                            position: 'relative'
                          }}
                        >
                          {/* Days in this month group with month label in the middle */}
                          {group.days.map((day, dayIndex) => (
                            <Box key={day.date} sx={{ position: 'relative' }}>
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  backgroundColor: getIntensityColor(day.count),
                                  borderRadius: 2,
                                  cursor: 'pointer',
                                  border: day.isToday ? '2px solid #000' : 'none',
                                  boxSizing: 'border-box',
                                  '&:hover': {
                                    opacity: 0.8,
                                    border: day.isToday ? '2px solid #000' : '1px solid rgba(0,0,0,0.2)'
                                  }
                                }}
                                title={`${day.date}: ${day.count} submission${day.count !== 1 ? 's' : ''}${day.isToday ? ' (Today)' : ''}`}
                              />
                              {/* Month label positioned in the middle of the group */}
                              {dayIndex === middleIndex && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.75rem',
                                    color: 'text.secondary',
                                    position: 'absolute',
                                    top: -18,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {group.month}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No submission activity in the last 30 days
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', marginTop: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Less
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#ebedf0', borderRadius: 2 }} />
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#c6e48b', borderRadius: 2 }} />
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#7bc96f', borderRadius: 2 }} />
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#239a3b', borderRadius: 2 }} />
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#196127', borderRadius: 2 }} />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  More
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Recent Submissions */}
          <Paper elevation={3} sx={{ padding: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Submissions
            </Typography>
            {stats.recentSubmissions && stats.recentSubmissions.length > 0 ? (
              <List>
                {stats.recentSubmissions.map((submission) => (
                  <Card
                    key={submission.submissionId}
                    sx={{
                      marginBottom: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 4
                      }
                    }}
                    onClick={() => navigate(`/question/${submission.questionId}`)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" gutterBottom>
                            {submission.questionTitle}
                            {submission.questionTitle.length >= 100 ? '...' : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatTimeAgo(submission.submittedAt)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {submission.isCorrect ? (
                            <CheckCircleIcon color="success" />
                          ) : (
                            <CancelIcon color="error" />
                          )}
                          <Chip
                            label={`${submission.score}/100`}
                            color={submission.score >= 80 ? 'success' : submission.score >= 60 ? 'warning' : 'error'}
                            size="small"
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ marginTop: 2 }}>
                No submissions yet. Start solving questions to see your progress here!
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Profile;

